import { useId } from 'react';
import { Icon } from '../../design-system/icons';
import type { CustomEditor, PropertyGroup } from './editor-types';
import { childPointer, getAt, type Pointer } from './json-pointer';
import { primitiveIssue, type PropertyDescriptor } from './schema-properties';
import { InfoTip } from './StudioPanels';

/**
 * Panneau de propriétés généré des descripteurs du schéma : un contrôle par sorte de propriété (texte,
 * nombre, oui / non, liste de valeurs, texte multilingue, objet imbriqué, liste de textes), un éditeur
 * dédié quand le métier en déclare un (expression, règle…). Aucun panneau écrit à la main par type de bloc.
 */

const LANGUAGES = ['fr', 'en'] as const;

export function PropertyPanel<D>({ group, document, onChange }: { group: PropertyGroup<D>; document: D; onChange: (pointer: Pointer, value: unknown, coalesce?: string) => void }) {
  return (
    <div className="ay-props">
      {group.title !== '' && <h3 className="ay-props__title">{group.title}</h3>}
      {group.descriptors.map((descriptor) => (
        <Property key={descriptor.key} descriptor={descriptor} path={descriptor.key} pointer={childPointer(group.base, descriptor.key)} group={group} document={document} onChange={onChange} />
      ))}
      {(group.reserved ?? []).map((tab) => (
        <section key={tab.id} className="ay-props__reserved" aria-disabled="true">
          <h4 className="ay-props__subtitle">{tab.label}</h4>
          <p className="ay-studio-empty">{tab.note}</p>
        </section>
      ))}
    </div>
  );
}

function labelOf(group: PropertyGroup<unknown>, path: string, key: string): string {
  return group.labels[path] ?? group.labels[key] ?? key;
}

function Property<D>({ descriptor, path, pointer, group, document, onChange }: { descriptor: PropertyDescriptor; path: string; pointer: Pointer; group: PropertyGroup<D>; document: D; onChange: (pointer: Pointer, value: unknown, coalesce?: string) => void }) {
  const id = useId();
  const value = getAt(document, pointer);
  const label = labelOf(group as PropertyGroup<unknown>, path, descriptor.key);
  const set = (next: unknown, coalesce?: string): void => onChange(pointer, next, coalesce);
  const custom: CustomEditor<D> | undefined = group.editors?.[path] ?? group.editors?.[`kind:${descriptor.kind}`];
  const help = descriptor.description;
  const issue = primitiveIssue(descriptor, value);

  if (custom !== undefined) {
    return <div className="ay-props__row">{custom({ descriptor, value, onChange: set, pointer, document, label })}</div>;
  }

  switch (descriptor.kind) {
    case 'boolean':
      return (
        <div className="ay-props__row ay-props__row--inline">
          <input id={id} type="checkbox" checked={value === true} onChange={(event) => set(event.target.checked ? true : undefined)} />
          <label htmlFor={id} className="ay-field__label">{label}</label>
          {help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}
        </div>
      );
    case 'enum': {
      const labels = group.enumLabels?.[path] ?? {};
      return (
        <div className="ay-props__row">
          <span className="ay-props__label"><label htmlFor={id} className="ay-field__label">{label}{descriptor.required && <span aria-hidden="true"> *</span>}</label>{help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}</span>
          <select id={id} value={value === undefined ? '' : String(value)} onChange={(event) => set(event.target.value === '' ? undefined : event.target.value)}>
            {!descriptor.required && <option value="">{descriptor.defaultValue !== undefined ? `Par défaut (${labels[String(descriptor.defaultValue)] ?? String(descriptor.defaultValue)})` : '—'}</option>}
            {(descriptor.enumValues ?? []).map((option) => (
              <option key={option} value={option}>{labels[option] ?? option}</option>
            ))}
          </select>
        </div>
      );
    }
    case 'number':
    case 'integer':
      return (
        <div className="ay-props__row">
          <span className="ay-props__label"><label htmlFor={id} className="ay-field__label">{label}{descriptor.required && <span aria-hidden="true"> *</span>}</label>{help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}</span>
          <input id={id} type="number" inputMode="decimal" step={descriptor.kind === 'integer' ? 1 : 'any'} min={descriptor.minimum} max={descriptor.maximum} value={typeof value === 'number' ? value : ''} aria-invalid={issue !== null && value !== undefined} onChange={(event) => set(event.target.value === '' ? undefined : Number(event.target.value), path)} />
          {issue !== null && value !== undefined && <span className="ay-field__error">{issue}</span>}
        </div>
      );
    case 'string':
      return (
        <div className="ay-props__row">
          <span className="ay-props__label"><label htmlFor={id} className="ay-field__label">{label}{descriptor.required && <span aria-hidden="true"> *</span>}</label>{help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}</span>
          <input id={id} type="text" value={typeof value === 'string' ? value : ''} aria-invalid={issue !== null} onChange={(event) => set(event.target.value === '' ? undefined : event.target.value, path)} />
          {issue !== null && <span className="ay-field__error">{issue}</span>}
        </div>
      );
    case 'localized':
      return <LocalizedInput label={label} required={descriptor.required} value={value} onChange={(next) => set(next, path)} />;
    case 'string_list':
      return <StringList label={label} value={Array.isArray(value) ? value.map(String) : []} onChange={(next) => set(next.length === 0 ? undefined : next, path)} />;
    case 'object': {
      const present = value !== undefined;
      return (
        <fieldset className="ay-props__group">
          <legend className="ay-props__subtitle">
            {label}
            {!descriptor.required && (
              <button type="button" className="ay-editor__tool" onClick={() => set(present ? undefined : {})} aria-label={present ? `Retirer ${label}` : `Ajouter ${label}`}>
                {present ? 'Retirer' : 'Ajouter'}
              </button>
            )}
          </legend>
          {present &&
            (descriptor.children ?? []).map((child) => (
              <Property key={child.key} descriptor={child} path={`${path}.${child.key}`} pointer={childPointer(pointer, child.key)} group={group} document={document} onChange={onChange} />
            ))}
        </fieldset>
      );
    }
    default:
      return (
        <div className="ay-props__row">
          <span className="ay-field__label">{label}</span>
          <span className="ay-studio-empty">{value === undefined ? 'Non réglé.' : 'Réglage avancé, gardé tel quel.'} Il se modifie dans le dépôt pour l’instant.</span>
        </div>
      );
  }
}

/** Texte multilingue (`localized_text`) : une saisie par langue, le français d'abord. */
export function LocalizedInput({ label, required, value, onChange }: { label: string; required: boolean; value: unknown; onChange: (value: unknown) => void }) {
  const id = useId();
  const current = value !== null && typeof value === 'object' ? (value as Record<string, string>) : {};
  const languages = [...new Set([...LANGUAGES, ...Object.keys(current)])];
  const set = (language: string, text: string): void => {
    const next = { ...current, [language]: text };
    if (text === '') delete next[language];
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };
  return (
    <fieldset className="ay-props__localized">
      <legend className="ay-field__label">{label}{required && <span aria-hidden="true"> *</span>}</legend>
      {languages.map((language) => (
        <span key={language} className="ay-props__lang">
          <label htmlFor={`${id}-${language}`} className="ay-props__lang-code">{language}</label>
          <input id={`${id}-${language}`} type="text" value={current[language] ?? ''} onChange={(event) => set(language, event.target.value)} aria-invalid={required && language === 'fr' && (current['fr'] ?? '') === ''} />
        </span>
      ))}
    </fieldset>
  );
}

function StringList({ label, value, onChange }: { label: string; value: string[]; onChange: (value: string[]) => void }) {
  const id = useId();
  return (
    <fieldset className="ay-props__group">
      <legend className="ay-field__label">{label}</legend>
      {value.map((entry, index) => (
        <span key={index} className="ay-props__list-row">
          <input id={`${id}-${index}`} aria-label={`${label} ${index + 1}`} type="text" value={entry} onChange={(event) => onChange(value.map((candidate, position) => (position === index ? event.target.value : candidate)))} />
          <button type="button" className="ay-editor__tool" aria-label={`Retirer ${entry || index + 1}`} onClick={() => onChange(value.filter((_, position) => position !== index))}><Icon name="close" size={14} /></button>
        </span>
      ))}
      <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange([...value, ''])}>+ Ajouter</button>
    </fieldset>
  );
}
